import { Typography, SxProps, Theme, Avatar } from '@mui/material';
import { memo } from 'react';
import { useRecordContext } from 'react-admin';
import AvatarField from './AvatarField';

interface UserFieldProps {
    size?: 'small' | 'medium' | 'large';
    source?: string;
    label?: string;
    sx?: SxProps<Theme>;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
};

const getSizePixels = (size?: string): number => {
    switch (size) {
        case 'small':
            return 32;
        case 'large':
            return 48;
        default:
            return 40;
    }
};

const UserField = (props: UserFieldProps) => {
    const { size, source = 'student', label, sx } = props;
    const record = useRecordContext<any>();

    if (!record) return null;

    const user = record[source];

    if (!user) return null;

    const userName = user?.name || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const userPhone = user?.phone;
    const userEmail = user?.email;
    const userImage = user?.profile_image || user?.image;
    const sizePixels = getSizePixels(size);

    return (
        <Typography
            variant="body2"
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
            component="div"
            sx={sx}
        >
            {userImage ? (
                <AvatarField
                    record={user}
                    size={size}
                    sx={{
                        mr: 1,
                        mt: -0.5,
                        mb: -0.5,
                    }}
                />
            ) : (
                <Avatar
                    sx={{
                        mr: 1,
                        mt: -0.5,
                        mb: -0.5,
                        width: sizePixels,
                        height: sizePixels,
                        bgcolor: '#1976d2',
                        color: '#fff',
                        fontSize: sizePixels / 2,
                    }}
                >
                    {getInitials(userName)}
                </Avatar>
            )}
            <div>
                <div style={{ fontWeight: 500 }}>{userName}</div>
                {userPhone && <div style={{ fontSize: '0.85em', color: '#666' }}>{userPhone}</div>}
                {userEmail && <div style={{ fontSize: '0.85em', color: '#666' }}>{userEmail}</div>}
            </div>
        </Typography>
    );
};

UserField.defaultProps = {
    source: 'student',
    label: 'User',
    size: 'medium',
};

export default memo(UserField);
